import {
  buildRules, formatDurationAmount, formatThresholdAmount
} from '../components/shared/severity-sensitivity/severity-sensitivity.util';
import {
  AlertSeverityEntry, AlertSeverityStatus, AlertSignalGroup, AlertType, AlertTypeCopy, AlertTypeResponse, AlertTypeSeverityConfig,
  GuidanceAnswer, Period, Sensitivity, SensitivityThreshold, Severity, SeverityBandLimit, SeverityConfigResponse,
  Signal, SignalTextPart, SignalThreshold, STORED_REVIEW_PAGES, StoredPersonWeek, StoredPersonWeekAnalysis,
  StoredPersonWeekCta, StoredPersonWeekDay, StoredPersonWeekDayIssue, StoredPersonWeekLimit, StoredPersonWeekMetricValue,
  StoredPersonWeekSignal, StoredReviewPage, UserAlertEntry,
  UserSignalGroup
} from '../models';

/**
 * Fixture weeks for the period picker (STAFF-145718 design handoff prototype). The signals/
 * threshold engine itself is live now (AA-138/AA-140) — this is the one piece of that original
 * fixture set still in use, since nothing backend-driven replaces these labels yet.
 */
export const PERIODS: Period[] = [
  { key: 'w0', label: 'Jul 27 – Aug 2' },
  { key: 'w1', label: 'Jul 20 – 26' },
  { key: 'w2', label: 'Jul 13 – 19' },
];

/**
 * Answers for "how does this work" / "how should I handle this" questions, which have no user
 * data behind them. Each one is honest about what the Help Center does and doesn't cover, then
 * links the articles it drew on.
 *
 * Every URL below was checked against support.timedoctor.com — don't add an entry with a guessed
 * article slug, the chip is a real outbound link. GUIDANCE_FALLBACK catches how-to questions that
 * match no topic, so no such question falls through to the data-shaped answer.
 */
export const GUIDANCE: GuidanceAnswer[] = [
  {
    keywords: ['low activity', 'activity level', 'inactive'],
    parts: [
      { text: 'The Help Center explains how low activity is calculated and reported, including the keyboard and mouse thresholds behind it, and how Activity Level is worked out. It doesn\'t cover how to raise low activity with someone, so treat those articles as background for the conversation rather than a script for it.' },
      { break: true },
      { text: 'Worth knowing before you open it: activity measures input, not output. Roles that work in long reading, review or call stretches can show low activity while the work is getting done.' },
    ],
    answer: 'The Help Center explains how low activity is calculated and reported, including the keyboard and mouse thresholds behind it, and how Activity Level is worked out. It doesn\'t cover how to raise low activity with someone, so treat those articles as background for the conversation rather than a script for it.\n\nWorth knowing before you open it: activity measures input, not output. Roles that work in long reading, review or call stretches can show low activity while the work is getting done.',
    docs: [
      {
        title: 'Understanding Low Activity Calculation and Reporting',
        url: 'https://support.timedoctor.com/knowledge/understanding-low-activity-calculation-and-reporting-in-time-doctor',
      },
      { title: '"Activity Level" explained', url: 'https://support.timedoctor.com/knowledge/activity-level-explained' },
    ],
  },
  {
    keywords: ['idle time', 'idle'],
    parts: [
      { text: 'The Help Center covers what counts as idle time and how the Activity Summary report presents it, including how the idle minutes and idle seconds percentages are each worked out. It doesn\'t set a target, because a healthy range depends on the role: client support tends to sit low, while sales can run much higher.' },
    ],
    answer: 'The Help Center covers what counts as idle time and how the Activity Summary report presents it, including how the idle minutes and idle seconds percentages are each worked out. It doesn\'t set a target, because a healthy range depends on the role: client support tends to sit low, while sales can run much higher.',
    docs: [
      {
        title: 'How to Understand Idle Times in the Activity Summary Report',
        url: 'https://support.timedoctor.com/knowledge/how-to-understand-idle-times-in-the-activity-summary-report',
      },
      { title: 'Video: Activity Summary Report', url: 'https://support.timedoctor.com/knowledge/activity-summary-page' },
    ],
  },
  {
    keywords: ['unusual activity', 'uar'],
    parts: [
      { text: 'The Help Center explains what the Unusual Activity report flags and how keyboard and mouse tracking feeds it. It describes the detection, not what to do about a flag, so read a flag as something to look into rather than a finding on its own.' },
    ],
    answer: 'The Help Center explains what the Unusual Activity report flags and how keyboard and mouse tracking feeds it. It describes the detection, not what to do about a flag, so read a flag as something to look into rather than a finding on its own.',
    docs: [
      { title: 'The Unusual Activity Report: A Quick Guide', url: 'https://support.timedoctor.com/knowledge/the-unusual-activity-report-a-quick-guide' },
      {
        title: 'How Does Keyboard and Mouse Activity Tracking Work in Time Doctor?',
        url: 'https://support.timedoctor.com/knowledge/how-does-keyboard-and-mouse-tracking-work-in-time-doctor',
      },
    ],
  },
];

export const GUIDANCE_FALLBACK: GuidanceAnswer = {
  keywords: [],
  parts: [
    { text: 'That one is about how Time Doctor works rather than what your data says, so there\'s nothing to read from this period. The Help Center is the better source, and the how-to guides below are the place to start.' },
  ],
  answer: 'That one is about how Time Doctor works rather than what your data says, so there\'s nothing to read from this period. The Help Center is the better source, and the how-to guides below are the place to start.',
  docs: [
    { title: 'How-to Guides', url: 'https://support.timedoctor.com/knowledge/how-to-guides' },
    { title: 'What reports does Time Doctor generate?', url: 'https://support.timedoctor.com/knowledge/what-reports-does-time-doctor-generate' },
  ],
};

export const SEV_ORDER: Record<string, number> = { info: 1, warning: 2, critical: 3 };
export const NOTCH_MIN: Record<string, number> = { quiet: 3, balanced: 2, sensitive: 1 };

// AA-212: attendance issues is out of Settings, but AskAiState.alertTypes is shared with the
// Needs You brief guidance panel (ask-ai-usage-menu) and signal fetching, which still need it —
// so it's filtered out at each display site rather than removed from the underlying data.
// AA-245: also excluded from alertTypeCount (state.ts) so the empty-state "N signal types" copy
// matches what Settings actually shows.
export const SETTINGS_EXCLUDED_ALERT_TYPES = new Set(['attendance_issues']);

// Frontend-authored copy for the alert types design has actually covered — keyed by id, looked up
// per live response in mapAlertTypeResponses. An id with no entry here (a brand new backend alert
// type) still renders, just with a generic derived name/rules/previews and no counts — see
// buildGenericCopy below. Only the fields with no backend source at all live here now; unit/x/y
// used to be hand-authored per entry too, before defaultMetric/defaultSensitivity existed.
const ALERT_TYPE_COPY: Record<string, AlertTypeCopy> = {
  idle_time: {
    name: 'Idle time', subject: 'Daily idle time', subjectVerb: 'is',
    supportedDirections: ['above'],
    previews: { label: 'idle time', frame: 'state', deltaWord: 'points', deltaHasOver: false },
  },
  heavy_hours: {
    name: 'Heavy hours', subject: 'Daily hours', subjectVerb: 'are',
    supportedDirections: ['above'],
    previews: { label: 'heavy hours' },
  },
  attendance_issues: {
    name: 'Attendance issues', subject: 'Late or missing hours in a week', subjectVerb: 'are',
    supportedDirections: ['above'],
    previews: { label: 'attendance issues' },
  },
  unusual_activity: {
    name: 'Unusual activity', subject: 'Daily unusual activity', subjectVerb: 'is',
    supportedDirections: ['above'],
    previews: { label: 'unusual activity' },
  },
  manual_time: {
    name: 'Manual time added', subject: 'Daily manual time added', subjectVerb: 'is',
    supportedDirections: ['above'],
    previews: { label: 'manual time' },
  },
  mobile_time: {
    name: 'Mobile time added', subject: 'Daily mobile time added', subjectVerb: 'is',
    supportedDirections: ['above'],
    previews: { label: 'mobile time' },
  },
};

// Only used when the live GET /alerts response isn't available yet (endpoint down, or the
// synchronous "haven't loaded" defaults in store/model.ts) — the numbers ALERT_TYPE_COPY's six
// known ids would otherwise get from a real response. defaultSensitivity's quiet/balanced values
// are what used to be hand-typed x/y guesses, just expressed the way the real endpoint sends them;
// sensitive: 0 matches the convention the real response itself uses (the sensitive notch reads off
// the live threshold, not an offset — see buildPreviews).
const FALLBACK_ALERT_TYPE_RESPONSES: AlertTypeResponse[] = [
  {
    // Only used when the live GET /alerts response isn't available yet — the configured idle limit
    // (40%) the six known ids would otherwise get from a real response.
    id: 'idle_time', title: '', suggestedCopilotPrompt: '', isCreated: true, isEnabled: true,
    defaultOperator: 'above', defaultThreshold: 40, defaultMetric: 'percentage',
    defaultExceedLimits: { info: { min: 0, max: 10 }, warning: { min: 10, max: 20 }, critical: { min: 20, max: null } },
    defaultSensitivity: [{ name: 'quiet', value: 20 }, { name: 'balanced', value: 10 }, { name: 'sensitive', value: 0 }],
    severityConfigs: [{
      userId: null, sensitivity: { name: 'balanced', value: 40 }, threshold: 40, operator: 'above',
      exceedLimits: { info: { min: 0, max: 10 }, warning: { min: 10, max: 20 }, critical: { min: 20, max: null } },
    }],
    createdAt: '2026-08-17T06:28:27.630Z', updatedAt: '2026-08-17T06:28:27.630Z',
  },
  {
    id: 'heavy_hours', title: '', suggestedCopilotPrompt: '', isCreated: true, isEnabled: true,
    defaultOperator: 'above', defaultThreshold: 8, defaultMetric: 'hours',
    defaultExceedLimits: { info: { min: 0, max: 4 }, warning: { min: 4, max: 8 }, critical: { min: 8, max: null } },
    defaultSensitivity: [{ name: 'quiet', value: 8 }, { name: 'balanced', value: 4 }, { name: 'sensitive', value: 0 }],
    severityConfigs: [{
      userId: null, sensitivity: { name: 'balanced', value: 8 }, threshold: 8, operator: 'above',
      exceedLimits: { info: { min: 0, max: 4 }, warning: { min: 4, max: 8 }, critical: { min: 8, max: null } },
    }],
    createdAt: '2026-08-17T06:28:27.630Z', updatedAt: '2026-08-17T06:28:27.630Z',
  },
  {
    id: 'attendance_issues', title: '', suggestedCopilotPrompt: '', isCreated: true, isEnabled: true,
    defaultOperator: 'above', defaultThreshold: 4, defaultMetric: 'hours',
    defaultExceedLimits: { info: { min: 0, max: 2 }, warning: { min: 2, max: 6 }, critical: { min: 6, max: null } },
    defaultSensitivity: [{ name: 'quiet', value: 6 }, { name: 'balanced', value: 2 }, { name: 'sensitive', value: 0 }],
    severityConfigs: [{
      userId: null, sensitivity: { name: 'balanced', value: 4 }, threshold: 4, operator: 'above',
      exceedLimits: { info: { min: 0, max: 2 }, warning: { min: 2, max: 6 }, critical: { min: 6, max: null } },
    }],
    createdAt: '2026-08-17T06:28:27.630Z', updatedAt: '2026-08-17T06:28:27.630Z',
  },
  {
    id: 'unusual_activity', title: '', suggestedCopilotPrompt: '', isCreated: true, isEnabled: true,
    defaultOperator: 'above', defaultThreshold: 0, defaultMetric: 'hours',
    defaultExceedLimits: { info: { min: 0, max: 4 }, warning: { min: 4, max: 10 }, critical: { min: 10, max: null } },
    defaultSensitivity: [{ name: 'quiet', value: 10 }, { name: 'balanced', value: 4 }, { name: 'sensitive', value: 0 }],
    severityConfigs: [{
      userId: null, sensitivity: { name: 'balanced', value: 0 }, threshold: 0, operator: 'above',
      exceedLimits: { info: { min: 0, max: 4 }, warning: { min: 4, max: 10 }, critical: { min: 10, max: null } },
    }],
    createdAt: '2026-08-17T06:28:27.630Z', updatedAt: '2026-08-17T06:28:27.630Z',
  },
  {
    id: 'manual_time', title: '', suggestedCopilotPrompt: '', isCreated: true, isEnabled: true,
    defaultOperator: 'above', defaultThreshold: 1, defaultMetric: 'hours',
    defaultExceedLimits: { info: { min: 0, max: 3 }, warning: { min: 3, max: 8 }, critical: { min: 8, max: null } },
    defaultSensitivity: [{ name: 'quiet', value: 8 }, { name: 'balanced', value: 3 }, { name: 'sensitive', value: 0 }],
    severityConfigs: [{
      userId: null, sensitivity: { name: 'balanced', value: 1 }, threshold: 1, operator: 'above',
      exceedLimits: { info: { min: 0, max: 3 }, warning: { min: 3, max: 8 }, critical: { min: 8, max: null } },
    }],
    createdAt: '2026-08-17T06:28:27.630Z', updatedAt: '2026-08-17T06:28:27.630Z',
  },
  {
    id: 'mobile_time', title: '', suggestedCopilotPrompt: '', isCreated: true, isEnabled: true,
    defaultOperator: 'above', defaultThreshold: 1, defaultMetric: 'hours',
    defaultExceedLimits: { info: { min: 0, max: 2 }, warning: { min: 2, max: 6 }, critical: { min: 6, max: null } },
    defaultSensitivity: [{ name: 'quiet', value: 6 }, { name: 'balanced', value: 2 }, { name: 'sensitive', value: 0 }],
    severityConfigs: [{
      userId: null, sensitivity: { name: 'balanced', value: 1 }, threshold: 1, operator: 'above',
      exceedLimits: { info: { min: 0, max: 2 }, warning: { min: 2, max: 6 }, critical: { min: 6, max: null } },
    }],
    createdAt: '2026-08-17T06:28:27.630Z', updatedAt: '2026-08-17T06:28:27.630Z',
  },
];

// 'ontrack_time_change' -> 'Ontrack time change' — last-resort name for an id with neither
// ALERT_TYPE_COPY nor a usable response.title.
function humanizeId(id: string): string {
  const words = id.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Display unit for a backend defaultMetric key. Falls back to the raw key for anything not yet
// mapped, rather than showing nothing — a fixed, known set today ('percentage'/'hours'/'days');
// growing that set only needs a new case here, not a change anywhere severity is rendered.
function metricToUnit(metric: string): string {
  switch (metric) {
    // AA-212: the live GET /alerts response for idle_time sends 'percent', not 'percentage' — both
    // map to '%' so severity-sensitivity.util.ts's percentage-vs-duration branching (formatAmount,
    // thresholdLimits) doesn't mistake a percent threshold for an hours one.
    case 'percentage':
    case 'percent':
      return '%';
    case 'hours': return 'h';
    case 'days': return 'days';
    default: return metric;
  }
}

// The severityConfig to render for an alert type: an exact per-user override if one exists, else
// the company-wide default (userId: null). Before an alert type has ever been created (isCreated:
// false), severityConfigs is empty — the response's own default* fields fill the same shape (and a
// synthesized 'balanced' sensitivity, matching the app's own overall default notch) so there's
// still something to render (and to seed the create call's body, see
// AskAiService.provisionUncreatedAlerts) before the create round-trips.
function severityConfigFor(response: AlertTypeResponse, currentUserId: string | undefined): AlertTypeSeverityConfig {
  return response.severityConfigs.find(c => c.userId === currentUserId)
    ?? response.severityConfigs.find(c => c.userId === null)
    ?? {
    userId: null, sensitivity: { name: 'balanced', value: response.defaultThreshold },
    threshold: response.defaultThreshold, operator: response.defaultOperator, exceedLimits: response.defaultExceedLimits,
  };
}

// The threshold defaultSensitivity gives a notch, or `fallback` if that notch isn't in the array
// (e.g. a malformed/older response parseAlertTypeResponse let through with defaultSensitivity: []).
function sensitivityDefault(response: AlertTypeResponse, notch: Sensitivity, fallback: number): number {
  return response.defaultSensitivity.find(s => s.name === notch)?.value ?? fallback;
}

/**
 * Resolves each alert type's severityConfigs against `currentUserId` into the `AlertType`
 * components render. Pass real GET /alerts responses once fetched (AskAiService.getAlertTypes
 * does) — every response contributes an AlertType, whether or not ALERT_TYPE_COPY has hand-authored
 * copy for its id. Omitted, this reads purely off FALLBACK_ALERT_TYPE_RESPONSES — used for the
 * synchronous defaults in store/model.ts and as the "haven't loaded yet" state.
 */
export function mapAlertTypeResponses(currentUserId: string | undefined, responses?: AlertTypeResponse[]): AlertType[] {
  const source = responses && responses.length ? responses : FALLBACK_ALERT_TYPE_RESPONSES;
  return source.map(response => {
    const config = severityConfigFor(response, currentUserId);
    const severity = {
      threshold: config.threshold,
      unit: metricToUnit(response.defaultMetric),
      direction: config.operator,
      // x/y feed buildPreviews'/buildRules' "About X+ over"/"Only Y+ over" copy — the
      // balanced/quiet notch thresholds, real ones now (defaultSensitivity), falling back to 0
      // only for a malformed response with none.
      x: sensitivityDefault(response, 'balanced', 0),
      y: sensitivityDefault(response, 'quiet', 0),
    };
    const copy = ALERT_TYPE_COPY[response.id];
    const name = copy?.name ?? (response.title || humanizeId(response.id));
    // The `label` a generic previews/rules pair reads as ("Flag idle time whenever...") — the
    // hand-authored one if `previews` already carries it, else the display name lowercased.
    const label = copy?.previews && 'label' in copy.previews ? copy.previews.label : name.toLowerCase();
    const previews = copy?.previews ?? { label };
    return {
      key: response.id,
      name,
      subject: copy?.subject ?? name,
      subjectVerb: copy?.subjectVerb ?? 'is',
      severity,
      supportedDirections: copy?.supportedDirections ?? ['above', 'below'],
      notchLabels: copy?.notchLabels,
      rules: copy?.rules ?? buildRules(severity, 'label' in previews ? previews : { label }),
      previews,
      // No authored counts and no real counts endpoint to compute one — blank rather than a
      // fabricated number; the template hides the count line entirely when this is empty.
      counts: copy?.counts ?? { sensitive: '', balanced: '', quiet: '' },
      // The notch actually applied to this config if the response says so, else the app's own
      // overall default.
      mode: config.sensitivity?.name ?? 'balanced',
      isEnabled: response.isEnabled,
      defaultSensitivity: response.defaultSensitivity,
    };
  });
}

function parseSeverityBandLimit(raw: unknown): SeverityBandLimit | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.min !== 'number') return null;
  if (o.max !== null && typeof o.max !== 'number') return null;
  return { min: o.min, max: o.max };
}

function parseSeverityConfig(raw: unknown): SeverityConfigResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.userId !== null && typeof o.userId !== 'string') return null;
  if (typeof o.threshold !== 'number') return null;
  if (o.operator !== 'above' && o.operator !== 'below') return null;
  if (!o.exceedLimits || typeof o.exceedLimits !== 'object') return null;
  const el = o.exceedLimits as Record<string, unknown>;
  const info = parseSeverityBandLimit(el.info);
  const warning = parseSeverityBandLimit(el.warning);
  const critical = parseSeverityBandLimit(el.critical);
  if (!info || !warning || !critical) return null;
  return { userId: o.userId, threshold: o.threshold, operator: o.operator, exceedLimits: { info, warning, critical } };
}

const SENSITIVITY_VALUES: Sensitivity[] = ['quiet', 'balanced', 'sensitive'];

function parseSensitivityThreshold(raw: unknown): SensitivityThreshold | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.name !== 'string' || !SENSITIVITY_VALUES.includes(o.name as Sensitivity)) return null;
  if (typeof o.value !== 'number') return null;
  return { name: o.name as Sensitivity, value: o.value };
}

// AlertTypeResponse's severityConfigs entries — reuses parseSeverityConfig for the shared
// fields, then requires sensitivity on top; an entry missing/malformed there is dropped like any
// other invalid one.
function parseAlertTypeSeverityConfig(raw: unknown): AlertTypeSeverityConfig | null {
  const base = parseSeverityConfig(raw);
  if (!base) return null;
  const sensitivity = parseSensitivityThreshold((raw as Record<string, unknown>).sensitivity);
  if (!sensitivity) return null;
  return { ...base, sensitivity };
}

function parseAlertTypeResponse(raw: unknown): AlertTypeResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id.trim()) return null;
  if (o.defaultOperator !== 'above' && o.defaultOperator !== 'below') return null;
  if (typeof o.defaultThreshold !== 'number') return null;
  if (!o.defaultExceedLimits || typeof o.defaultExceedLimits !== 'object') return null;
  const del = o.defaultExceedLimits as Record<string, unknown>;
  const defaultInfo = parseSeverityBandLimit(del.info);
  const defaultWarning = parseSeverityBandLimit(del.warning);
  const defaultCritical = parseSeverityBandLimit(del.critical);
  // No default threshold config at all — nothing for mapAlertTypeResponses to resolve or for
  // provisionUncreatedAlerts to seed a create call with, so this id falls back to the ALERT_TYPES
  // fixture's own mock data instead of a broken entry.
  if (!defaultInfo || !defaultWarning || !defaultCritical) return null;
  // severityConfigs is legitimately empty until this alert type is created (isCreated: false) —
  // unlike defaultExceedLimits above, an empty array here isn't a parse failure.
  const severityConfigs = Array.isArray(o.severityConfigs)
    ? o.severityConfigs.map(parseAlertTypeSeverityConfig).filter((c): c is AlertTypeSeverityConfig => c !== null)
    : [];
  // Same leniency as severityConfigs: an alert type with no defaultSensitivity (or a malformed one)
  // still parses — mapAlertTypeResponses' sensitivityDefault falls back to 0 per notch rather than
  // this id dropping out entirely over one non-critical field.
  const defaultSensitivity = Array.isArray(o.defaultSensitivity)
    ? o.defaultSensitivity.map(parseSensitivityThreshold).filter((s): s is SensitivityThreshold => s !== null)
    : [];
  return {
    id: o.id,
    title: typeof o.title === 'string' ? o.title : '',
    suggestedCopilotPrompt: typeof o.suggestedCopilotPrompt === 'string' ? o.suggestedCopilotPrompt : '',
    defaultOperator: o.defaultOperator,
    defaultThreshold: o.defaultThreshold,
    defaultMetric: typeof o.defaultMetric === 'string' ? o.defaultMetric : '',
    defaultExceedLimits: { info: defaultInfo, warning: defaultWarning, critical: defaultCritical },
    defaultSensitivity,
    isCreated: typeof o.isCreated === 'boolean' ? o.isCreated : false,
    isEnabled: typeof o.isEnabled === 'boolean' ? o.isEnabled : false,
    severityConfigs,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : '',
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : '',
  };
}

/** Validates the untrusted GET /alerts JSON array into AlertTypeResponse[], dropping (not
 * throwing on) any entry that doesn't match the shape — same defensive posture as
 * AgentGatewayService's other response parsers. */
export function parseAlertTypeResponses(raw: unknown[]): AlertTypeResponse[] {
  return raw.map(parseAlertTypeResponse).filter((r): r is AlertTypeResponse => r !== null);
}

export const RESPONSE_SEVERITY_VALUES: Severity[] = ['critical', 'warning', 'info'];

// The alert types whose `ratio` is a formatted "8h 9m" duration rather than a plain number (see
// parseRatioValue below) — buildGroupedSignal uses this to render their rows/chart/body copy back
// out as "8h 09m" (formatDurationAmount) instead of the generic decimal-hour formatThresholdAmount.
const DURATION_RATIO_ALERT_IDS = new Set(['unusual_activity', 'manual_time', 'mobile_time']);

// ratio is a plain number for attendance_issues/heavy_hours/idle_time, but arrives as a formatted
// duration string (e.g. "8h 9m", "0m", "<1m") for manual_time/mobile_time/unusual_activity —
// parses either shape into decimal hours, or null for anything else so the caller can keep
// dropping (not guessing at) entries that don't match the contract. Mirrors
// formatDurationFromHours' (ai-response.types.ts) output shape in reverse.
export function parseRatioValue(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed === '<1m') return 1 / 60;
  const match = trimmed.match(/^(?:(\d+)h)?\s*(?:(\d+)m)?$/i);
  if (!match || (!match[1] && !match[2])) return null;
  const hours = match[1] ? Number(match[1]) : 0;
  const minutes = match[2] ? Number(match[2]) : 0;
  return hours + minutes / 60;
}

function parseAlertSeverityEntry(raw: unknown): AlertSeverityEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id.trim()) return null;
  if (typeof o.severity !== 'string' || !RESPONSE_SEVERITY_VALUES.includes(o.severity as Severity)) return null;
  const ratio = parseRatioValue(o.ratio);
  if (ratio == null) return null;
  return {
    id: o.id,
    severity: o.severity as Severity,
    ratio,
    // Preserved verbatim (see AlertSeverityEntry.ratioDisplay) only when the response actually
    // sent a duration string — buildGroupedSignal renders this as-is rather than reformatting
    // `ratio` back into text.
    ratioDisplay: typeof o.ratio === 'string' ? o.ratio.trim() : undefined,
    signalId: typeof o.signalId === 'string' ? o.signalId : '',
    analyzedCount: typeof o.analyzedCount === 'number' ? o.analyzedCount : 0,
    status: parseAlertSeverityStatus(o.status),
  };
}

// See AlertSeverityEntry.status — drops anything that doesn't match rather than guessing at it.
function parseAlertSeverityStatus(raw: unknown): AlertSeverityStatus | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  if (o.status !== 'active' && o.status !== 'dismissed') return undefined;
  return {
    status: o.status,
    updatedBy: typeof o.updatedBy === 'string' ? o.updatedBy : '',
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : '',
  };
}

// Each SignalsResponse.signals[] entry is a single-key object keyed by that alert type's id —
// reject anything else (wrong shape entirely, or more/fewer than one key) rather than guessing
// which key was meant.
function parseSignalGroup(raw: unknown): AlertSignalGroup | null {
  if (!raw || typeof raw !== 'object') return null;
  const keys = Object.keys(raw as Record<string, unknown>);
  if (keys.length !== 1) return null;
  const alertId = keys[0];
  if (!alertId.trim()) return null;
  const buckets = (raw as Record<string, unknown>)[alertId];
  if (!buckets || typeof buckets !== 'object') return null;
  // Re-bucketed by each entry's own `severity` field rather than trusted from the response's
  // bucket key it came from (defensive, same posture as every other parser here) — an 'ok' (not
  // flagged) entry, or anything else parseAlertSeverityEntry doesn't recognize, drops out here.
  const bySeverity: Partial<Record<Severity, AlertSeverityEntry[]>> = {};
  Object.values(buckets as Record<string, unknown>).forEach(list => {
    if (!Array.isArray(list)) return;
    list.forEach(item => {
      const entry = parseAlertSeverityEntry(item);
      if (!entry) return;
      (bySeverity[entry.severity] ??= []).push(entry);
    });
  });
  return { alertId, bySeverity };
}

/** Validates the untrusted GET activity/agent-gateway/signals JSON body ({ signals: [...] }) into
 * AlertSignalGroup[], dropping (not throwing on) any entry that doesn't match the shape. */
export function parseSignalsResponse(raw: unknown): AlertSignalGroup[] {
  const body = raw as { signals?: unknown } | null;
  if (!body || !Array.isArray(body.signals)) return [];
  return body.signals.map(parseSignalGroup).filter((g): g is AlertSignalGroup => g !== null);
}

// Includes 'ok' (unlike RESPONSE_SEVERITY_VALUES) — AA-214's per-day aggregation needs every day's
// entry, breaching or not, to compute a real daily chart. See UserAlertEntry.severity.
const USER_ALERT_SEVERITY_VALUES: (Severity | 'ok')[] = ['ok', ...RESPONSE_SEVERITY_VALUES];

// Full lowercase weekday names, as UserAlertEntry.day arrives — validated here so a bad value drops
// the entry before ask-ai-person-signals.util.ts maps it to the short Mon..Sun form.
const WEEKDAY_FULL_VALUES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Unlike severity/ratio, a bad `day` doesn't drop the whole entry — it's treated as absent
// (undefined). See UserAlertEntry.day.
function parseUserAlertDay(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const day = raw.trim().toLowerCase();
  return WEEKDAY_FULL_VALUES.includes(day) ? day : undefined;
}

function parseUserAlertEntry(alertId: string, raw: unknown): UserAlertEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.severity !== 'string' || !USER_ALERT_SEVERITY_VALUES.includes(o.severity as Severity | 'ok')) return null;
  const ratio = parseRatioValue(o.ratio);
  if (ratio == null) return null;
  return {
    alertId,
    severity: o.severity as Severity | 'ok',
    ratio,
    ratioDisplay: typeof o.ratio === 'string' ? o.ratio.trim() : undefined,
    signalId: typeof o.signalId === 'string' ? o.signalId : '',
    analyzedCount: typeof o.analyzedCount === 'number' ? o.analyzedCount : 0,
    day: parseUserAlertDay(o.day),
    status: parseAlertSeverityStatus(o.status),
  };
}

function parseUserSignalGroup(raw: unknown): UserSignalGroup | null {
  if (!raw || typeof raw !== 'object') return null;
  const keys = Object.keys(raw as Record<string, unknown>);
  if (keys.length !== 1) return null;
  const userId = keys[0];
  if (!userId.trim()) return null;
  const list = (raw as Record<string, unknown>)[userId];
  if (!Array.isArray(list)) return null;
  const entries = list
    .map(item => {
      const alertId = item && typeof item === 'object' ? (item as Record<string, unknown>).alertId : undefined;
      return typeof alertId === 'string' && alertId.trim() ? parseUserAlertEntry(alertId, item) : null;
    })
    .filter((e): e is UserAlertEntry => e !== null);
  return { userId, entries };
}

/** Validates the untrusted GET activity/agent-gateway/signals?groupBy=user JSON body
 * ({ signals: [...] }) into UserSignalGroup[], dropping (not throwing on) any entry that doesn't
 * match the shape — same defensive posture as parseSignalsResponse. */
export function parseUserGroupedSignalsResponse(raw: unknown): UserSignalGroup[] {
  const body = raw as { signals?: unknown } | null;
  if (!body || !Array.isArray(body.signals)) return [];
  return body.signals.map(parseUserSignalGroup).filter((g): g is UserSignalGroup => g !== null);
}

// GET activity/agent-gateway/signals carries no per-user threshold/operator override alongside
// its results any more (see AlertSignalGroup) — the direction word ("above"/"below") a grouped
// card's title needs falls back to the alert type's own FALLBACK_ALERT_TYPE_RESPONSES.
// defaultOperator (same fixture fallback already used for `unit` below), same as
// AskAiService.getAlertTypes falls back to that fixture wholesale when the live alerts endpoint
// is down. TODO: swap for the company's actual current AlertType.severity.direction (from
// AskAiService.getAlertTypes) if a company ever configures a non-default direction — that would
// need threading AlertType[] into getSignals, not done yet since nothing here does today either.
function pluralPeople(count: number): { noun: string, verb: string } {
  return count === 1 ? { noun: '1 person', verb: 'is' } : { noun: `${count} people`, verb: 'are' };
}

// Builds one Signal per (alertId, severity) group — "N people are above/below your threshold" —
// rather than one Signal per flagged user, so Needs You shows a single card for a whole group
// instead of one per person (see needs-you-brief's grouping). `rows` still lists every person in
// the group (name + their own ratio) for the "View details"/evidence breakdown table. `entry.id`
// is a userId, not a display name — `userNames` (built via ResolveService, see
// AskAiService.getSignals) resolves it to one everywhere it's shown, falling back to the raw id
// for a user that lookup couldn't resolve (e.g. deleted). `name`/`unit`/direction fall back to a
// humanized id/raw metric key/'above' when this alertId has neither ALERT_TYPE_COPY nor a
// FALLBACK_ALERT_TYPE_RESPONSES entry — AlertSignalGroup carries no title of its own to fall back
// to the way AlertTypeResponse-driven copy can.
function buildGroupedSignal(
  alertId: string, severity: Severity, entries: AlertSeverityEntry[], period: string, userNames: Map<string, string>,
  screencastsAllowed: Map<string, boolean>,
  thresholds?: Record<string, SignalThreshold>,
): Signal {
  const name = ALERT_TYPE_COPY[alertId]?.name ?? humanizeId(alertId);
  const fallback = FALLBACK_ALERT_TYPE_RESPONSES.find(r => r.id === alertId);
  // The threshold/condition actually configured for this alert type in Settings. Falls back to the
  // alert type's shipped default only when no config reached us — never prefer the default over a
  // configured value, or a card claims a line the user never set (e.g. plotting against 45h while
  // Settings says 0h).
  const configured = thresholds?.[alertId];
  const unit = configured?.unit ?? metricToUnit(fallback?.defaultMetric ?? '');
  const direction = configured?.direction ?? fallback?.defaultOperator ?? 'above';
  const threshold = configured?.threshold ?? fallback?.defaultThreshold;
  // See DURATION_RATIO_ALERT_IDS — these three's `threshold`/delta (config values and a client-side
  // diff, neither of which has any raw response text) still render as "8h 09m" via
  // formatDurationAmount; entry.ratioDisplay below covers the actual per-user amount, verbatim.
  const isDurationRatio = DURATION_RATIO_ALERT_IDS.has(alertId);
  const formatAmount = (value: number) => (isDurationRatio ? formatDurationAmount(value) : formatThresholdAmount(value, unit));
  const { noun, verb } = pluralPeople(entries.length);
  const body = `${noun} ${verb} ${direction} the ${name.toLowerCase()} threshold.`;
  // One bar per flagged person, worst breach first — `ratio` is already the per-user value the
  // threshold chart plots against `threshold` below. `display`, when set, is the response's own
  // "8h 9m" text for that person, preferred verbatim over reformatting `value`.
  const userBars = entries
    .map(entry => ({
      id: entry.id, name: userNames.get(entry.id) ?? entry.id, value: entry.ratio, display: entry.ratioDisplay,
    }))
    .sort((a, b) => (direction === 'below' ? a.value - b.value : b.value - a.value));
  const rows = entries.map(entry => {
    const userName = userNames.get(entry.id) ?? entry.id;
    // The response's own text, verbatim, when it sent one — only the delta below (which the
    // response never carries) falls back to reformatting a number.
    const amount = entry.ratioDisplay ?? formatAmount(entry.ratio);
    // The response carries no per-user threshold, so `threshold` above (the configured
    // company-wide value) is what "how far over" is measured against — not exact for a user with
    // a per-user override.
    const sign = direction === 'below' ? -1 : 1;
    // formatAmount already renders a negative diff with its own "-" — don't also prefix "+" in
    // that case, or a per-user override that puts someone under the fallback threshold shows a
    // double sign ("+-3.4h").
    const diff = threshold != null ? sign * (entry.ratio - threshold) : null;
    const delta = diff != null ? `${diff >= 0 ? '+' : ''}${formatAmount(diff)}` : '';
    return {
      name: userName, value: amount, delta, detail: `${name} for ${userName} is ${amount}.`,
      screencastsAllowed: screencastsAllowed.get(entry.id),
    };
  });

  return {
    id: `${alertId}-${severity}`,
    // Filtered defensively — parseAlertSeverityEntry falls back to '' for a missing signalId
    // rather than dropping the whole entry, and that empty string must never reach the dismiss API.
    signalIds: entries.map(entry => entry.signalId).filter(id => id.trim().length > 0),
    key: alertId,
    type: name,
    sev: severity,
    body,
    parts: [{ text: body }],
    answerParts: [{ text: body }],
    answer: body,
    // Critical renders its chart immediately, warning behind the "Show the chart" toggle, info
    // never — see NeedsYouBriefComponent.showTrend.
    trend: severity === 'critical' || severity === 'warning',
    chartCaption: '',
    bars: [],
    userCount: entries.length,
    userName: entries.length === 1 ? userBars[0]?.name : undefined,
    userBars,
    threshold: threshold ?? 0,
    unit,
    durationRatio: isDurationRatio,
    direction,
    askPrompt: entries.length === 1
      ? `What is driving the ${name.toLowerCase()} for this person?`
      : `What is driving the ${name.toLowerCase()} for these ${entries.length} people?`,
    recommendation: '',
    draftMessage: '',
    followUps: [],
    reportName: '',
    reportUrl: '',
    chipContext: `${noun}, ${period}`,
    evidenceCount: entries.length,
    period,
    metricLabel: name,
    evBody: body,
    rows,
    rowLabel: 'Person',
    single: false,
  };
}

/**
 * Turns GET activity/agent-gateway/signals groups into the Signal[] the Needs You brief renders —
 * one grouped Signal per (alertId, severity) bucket rather than one per flagged user, per the
 * "group by severity + type" behavior the brief now uses to keep a busy company's list readable.
 * Every group/severity bucket contributes, whether or not its alertId has hand-authored copy —
 * see buildGroupedSignal's generic-name fallback. `userNames` maps each flagged userId to its
 * display name, and `screencastsAllowed` to whether the viewer may see that user's screencasts
 * (both resolved via ResolveService/AuthState in AskAiService.getSignals before calling this) —
 * entry.id has no display name or permission of its own.
 */
export function buildSignalsFromSignalsResponse(
  groups: AlertSignalGroup[], period: string, userNames: Map<string, string>, screencastsAllowed: Map<string, boolean>,
  thresholds?: Record<string, SignalThreshold>,
): Signal[] {
  const signals: Signal[] = [];
  groups.forEach(group => {
    (Object.keys(group.bySeverity) as Severity[]).forEach(severity => {
      const entries = group.bySeverity[severity];
      if (entries?.length) {
        signals.push(buildGroupedSignal(group.alertId, severity, entries, period, userNames, screencastsAllowed, thresholds));
      }
    });
  });
  return signals;
}

// ---- AA-247 saved person-week cards ---------------------------------------------------------
// Parse SignalsResponse.personWeeks into StoredPersonWeek[] the Needs you brief adapts into
// Iulian's card design (person-week-preview.ts). Malformed rows/fields are dropped rather than
// throwing, so one bad card never blanks the feed.

const STORED_WEEK_STATUS = ['none', 'ready', 'failed'] as const;
const STORED_WEEK_SEV: Severity[] = ['critical', 'warning', 'info'];

function asObject(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
}

function parseStoredSeverity(value: unknown): Severity | undefined {
  return typeof value === 'string' && STORED_WEEK_SEV.includes(value as Severity) ? value as Severity : undefined;
}

function parseReviewPage(value: unknown): StoredReviewPage | undefined {
  return typeof value === 'string' && (STORED_REVIEW_PAGES as readonly string[]).includes(value)
    ? value as StoredReviewPage
    : undefined;
}

function parseStoredCta(raw: unknown): StoredPersonWeekCta | undefined {
  const o = asObject(raw);
  if (!o) return undefined;
  if (typeof o.key !== 'string' || !o.key.trim()) return undefined;
  if (typeof o.label !== 'string' || !o.label.trim()) return undefined;
  const page = parseReviewPage(o.page);
  return {
    key: o.key,
    label: o.label,
    href: typeof o.href === 'string' ? o.href : '',
    ...(page ? { page } : {}),
  };
}

function parseStoredMetricValue(raw: unknown): StoredPersonWeekMetricValue {
  const o = asObject(raw);
  return {
    value: typeof o?.value === 'number' ? o.value : 0,
    unit: typeof o?.unit === 'string' ? o.unit : '',
    display: typeof o?.display === 'string' ? o.display : undefined,
  };
}

function parseStoredLimit(raw: unknown): StoredPersonWeekLimit {
  const o = asObject(raw);
  return {
    value: typeof o?.value === 'number' ? o.value : 0,
    unit: typeof o?.unit === 'string' ? o.unit : '',
  };
}

function parseStringDates(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((d): d is string => typeof d === 'string' && !!d.trim()).map(d => d.trim());
}

function parseStoredSignalIds(raw: unknown): string[] {
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string' && !!id.trim()).map(id => id.trim());
}

function parseStoredSignal(raw: unknown): StoredPersonWeekSignal | null {
  const o = asObject(raw);
  if (!o || typeof o.type !== 'string' || !o.type.trim()) return null;
  const fromList = parseStoredSignalIds(o.signalIds);
  const fromSingular = parseStoredSignalIds(o.signalId);
  const signalIds = [...new Set([...fromList, ...fromSingular])];
  return {
    type: o.type,
    severity: parseStoredSeverity(o.severity) ?? 'info',
    daysOverLimit: typeof o.daysOverLimit === 'number' ? o.daysOverLimit : 0,
    limit: parseStoredLimit(o.limit),
    peakDay: typeof o.peakDay === 'string' ? o.peakDay : '',
    peakValue: parseStoredMetricValue(o.peakValue),
    deepLink: typeof o.deepLink === 'string' ? o.deepLink : undefined,
    firedDates: parseStringDates(o.firedDates),
    ...(signalIds.length ? { signalIds } : {}),
  };
}

/** Flatten every dismissable backend id on a person-week card (week-level + per-signal). */
export function collectPersonWeekSignalIds(week: StoredPersonWeek): string[] {
  const ids = [
    ...(week.signalIds ?? []),
    ...week.signals.flatMap(signal => signal.signalIds ?? []),
  ].filter(id => id.trim().length > 0);
  return [...new Set(ids)];
}

function parseStoredDayIssue(raw: unknown): StoredPersonWeekDayIssue | null {
  const o = asObject(raw);
  if (!o || typeof o.type !== 'string' || !o.type.trim()) return null;
  return {
    type: o.type,
    severity: parseStoredSeverity(o.severity) ?? 'info',
    value: parseStoredMetricValue(o.value),
    limit: parseStoredLimit(o.limit),
    deepLink: typeof o.deepLink === 'string' ? o.deepLink : undefined,
  };
}

function parseStoredMetrics(raw: unknown): Record<string, number> {
  const o = asObject(raw);
  if (!o) return {};
  const metrics: Record<string, number> = {};
  Object.keys(o).forEach(key => {
    if (typeof o[key] === 'number') metrics[key] = o[key] as number;
  });
  return metrics;
}

function parseStoredDay(raw: unknown): StoredPersonWeekDay | null {
  const o = asObject(raw);
  if (!o || typeof o.date !== 'string' || !o.date.trim()) return null;
  const issues = Array.isArray(o.issues)
    ? o.issues.map(parseStoredDayIssue).filter((i): i is StoredPersonWeekDayIssue => i !== null)
    : [];
  return { date: o.date.trim(), metrics: parseStoredMetrics(o.metrics), issues };
}

/** Every day a signal fired, unioned across signals; falls back to days[] carrying issues. */
function parseStoredFiredDates(signals: StoredPersonWeekSignal[], days: StoredPersonWeekDay[]): string[] {
  const dates = new Set<string>();
  signals.forEach(signal => signal.firedDates.forEach(date => dates.add(date)));
  if (!dates.size) {
    days.forEach(day => { if (day.issues.length) dates.add(day.date); });
  }
  return [...dates].sort();
}

function parseStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && !!v.trim()).map(v => v.trim());
}

function parseStoredPersonWeekAnalysis(raw: unknown): StoredPersonWeekAnalysis | null {
  const o = asObject(raw);
  if (!o) return null;
  const status = typeof o.status === 'string' && (STORED_WEEK_STATUS as readonly string[]).includes(o.status)
    ? o.status as StoredPersonWeekAnalysis['status']
    : 'none';
  const title = typeof o.title === 'string' ? o.title : '';
  const body = typeof o.body === 'string' ? o.body : '';
  // A ready write-up needs readable copy; drop empty shells so chat never seeds a blank turn.
  if (status === 'ready' && !title.trim() && !body.trim()) return null;
  return {
    status,
    analyzedAt: typeof o.analyzedAt === 'string' ? o.analyzedAt : undefined,
    model: typeof o.model === 'string' ? o.model : undefined,
    title,
    body,
    longTitle: typeof o.longTitle === 'string' ? o.longTitle : undefined,
    description: typeof o.description === 'string' ? o.description : undefined,
    signalsNamed: parseStringList(o.signalsNamed),
    contextChecked: parseStringList(o.contextChecked),
    followUpQuestion: typeof o.followUpQuestion === 'string' ? o.followUpQuestion : undefined,
    followUpAnswer: typeof o.followUpAnswer === 'string' ? o.followUpAnswer : undefined,
    dataCaveat: typeof o.dataCaveat === 'string' ? o.dataCaveat : (o.dataCaveat === null ? null : undefined),
    draftMessage: typeof o.draftMessage === 'string' ? o.draftMessage : undefined,
    primaryCta: parseStoredCta(o.primaryCta),
    secondaryCta: o.secondaryCta == null ? null : parseStoredCta(o.secondaryCta),
  };
}

function parseAnalysisByThreshold(raw: unknown): Record<string, StoredPersonWeekAnalysis> | undefined {
  const o = asObject(raw);
  if (!o) return undefined;
  const out: Record<string, StoredPersonWeekAnalysis> = {};
  Object.keys(o).forEach(key => {
    if (!key.trim()) return;
    const parsed = parseStoredPersonWeekAnalysis(o[key]);
    if (parsed) out[key] = parsed;
  });
  return Object.keys(out).length ? out : undefined;
}

/**
 * Pick the write-up that matches the viewer's threshold context. Tries `preferredKeys` in order
 * (typically auth user id, then company id); otherwise the first `ready` entry. Returns undefined
 * when nothing usable is present.
 */
export function pickPersonWeekAnalysis(
  byThreshold: Record<string, StoredPersonWeekAnalysis> | undefined,
  preferredKeys: readonly string[] = [],
): StoredPersonWeekAnalysis | undefined {
  if (!byThreshold) return undefined;
  for (const key of preferredKeys) {
    if (!key) continue;
    const hit = byThreshold[key];
    if (hit?.status === 'ready' && (hit.body.trim() || hit.title.trim())) return hit;
  }
  return Object.values(byThreshold).find(
    a => a.status === 'ready' && !!(a.body.trim() || a.title.trim()),
  );
}

/** Plain + bold parts for seeding the chat with a threshold analysis. */
export function personWeekAnalysisAnswerParts(analysis: StoredPersonWeekAnalysis): SignalTextPart[] {
  const parts: SignalTextPart[] = [];
  const title = (analysis.longTitle || analysis.title || '').trim();
  const body = (analysis.description || analysis.body || '').trim();
  if (title) {
    parts.push({ text: title, bold: true });
    if (body) parts.push({ break: true });
  }
  if (body) parts.push({ text: body });
  return parts;
}

/**
 * Chat opening answer parts from `followUpAnswer` (paragraph breaks preserved). Card prose stays
 * on `body` — do not use this for the Needs you card.
 */
export function personWeekFollowUpAnswerParts(followUpAnswer: string): SignalTextPart[] {
  const paragraphs = followUpAnswer.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const parts: SignalTextPart[] = [];
  paragraphs.forEach((paragraph, index) => {
    if (index) parts.push({ break: true });
    parts.push({ text: paragraph });
  });
  return parts;
}

function parseStoredPersonWeek(raw: unknown): StoredPersonWeek | null {
  const o = asObject(raw);
  if (!o || typeof o.user !== 'string' || !o.user.trim() || typeof o.title !== 'string') return null;
  const status = typeof o.analysisStatus === 'string' && (STORED_WEEK_STATUS as readonly string[]).includes(o.analysisStatus)
    ? o.analysisStatus as StoredPersonWeek['analysisStatus']
    : 'none';
  const signals = Array.isArray(o.signals)
    ? o.signals.map(parseStoredSignal).filter((s): s is StoredPersonWeekSignal => s !== null)
    : [];
  const days = Array.isArray(o.days)
    ? o.days.map(parseStoredDay).filter((d): d is StoredPersonWeekDay => d !== null)
    : [];
  const analysisByThreshold = parseAnalysisByThreshold(o.analysisByThreshold);
  const weekSignalIds = [...new Set([
    ...parseStoredSignalIds(o.signalIds),
    ...parseStoredSignalIds(o.signalId),
  ])];
  return {
    userId: o.user,
    userName: typeof o.userName === 'string' ? o.userName : o.user,
    weekStart: typeof o.weekStart === 'string' ? o.weekStart : '',
    weekEnd: typeof o.weekEnd === 'string' ? o.weekEnd : '',
    timezone: typeof o.timezone === 'string' ? o.timezone : undefined,
    severity: parseStoredSeverity(o.severity),
    rank: typeof o.rank === 'number' ? o.rank : null,
    title: o.title,
    body: typeof o.body === 'string' ? o.body : '',
    draftMessage: typeof o.draftMessage === 'string' ? o.draftMessage : undefined,
    followUpAnswer: typeof o.followUpAnswer === 'string' ? o.followUpAnswer : undefined,
    analysisStatus: status,
    primaryCta: parseStoredCta(o.primaryCta),
    secondaryCta: o.secondaryCta == null ? null : parseStoredCta(o.secondaryCta),
    signals,
    days,
    firedDates: parseStoredFiredDates(signals, days),
    ...(weekSignalIds.length ? { signalIds: weekSignalIds } : {}),
    ...(o.dismissed === true ? { dismissed: true } : {}),
    ...(analysisByThreshold ? { analysisByThreshold } : {}),
  };
}

/** Top-level `signals` on a person_week response: `{ [userId]: [{ alertId, signalId }] }`. */
function userSignalIdsFromFeed(raw: unknown): Map<string, { type: string, signalId: string }[]> {
  const byUser = new Map<string, { type: string, signalId: string }[]>();
  if (!Array.isArray(raw)) return byUser;
  raw.forEach(group => {
    if (!group || typeof group !== 'object') return;
    const record = group as Record<string, unknown>;
    const keys = Object.keys(record);
    if (keys.length !== 1) return;
    const userId = keys[0].trim();
    const list = record[keys[0]];
    if (!userId || !Array.isArray(list)) return;
    const entries: { type: string, signalId: string }[] = [];
    list.forEach(item => {
      if (!item || typeof item !== 'object') return;
      const entry = item as Record<string, unknown>;
      const type = typeof entry.alertId === 'string' ? entry.alertId.trim() : '';
      const signalId = typeof entry.signalId === 'string' ? entry.signalId.trim() : '';
      if (type && signalId) entries.push({ type, signalId });
    });
    if (entries.length) byUser.set(userId, [...(byUser.get(userId) ?? []), ...entries]);
  });
  return byUser;
}

/** Stamp each feed `signalId` onto the matching signal type so dismiss/undo can POST them. */
function attachFeedSignalIds(
  week: StoredPersonWeek, entries: { type: string, signalId: string }[] | undefined,
): StoredPersonWeek {
  if (!entries?.length) return week;
  const signals = week.signals.map(signal => {
    const extra = entries.filter(entry => entry.type === signal.type).map(entry => entry.signalId);
    if (!extra.length) return signal;
    return { ...signal, signalIds: [...new Set([...(signal.signalIds ?? []), ...extra])] };
  });
  const knownTypes = new Set(signals.map(signal => signal.type));
  const orphans = entries.filter(entry => !knownTypes.has(entry.type)).map(entry => entry.signalId);
  const signalIds = [...new Set([...(week.signalIds ?? []), ...orphans])];
  return { ...week, signals, ...(signalIds.length ? { signalIds } : {}) };
}

/** Saved person-week cards on GET activity/agent-gateway/signals, keyed by person id in feed
 * order. Missing or malformed rows are dropped. Also copies each user's feed signal ids
 * (`…_idle_time`, `…_heavy_hours`) onto the matching card so status updates have real ids. */
export function parseStoredPersonWeeks(raw: unknown): StoredPersonWeek[] {
  const body = raw as { personWeeks?: unknown, signals?: unknown } | null;
  if (!body || !Array.isArray(body.personWeeks)) return [];
  const feedIds = userSignalIdsFromFeed(body.signals);
  return body.personWeeks
    .map(week => {
      const parsed = parseStoredPersonWeek(week);
      return parsed ? attachFeedSignalIds(parsed, feedIds.get(parsed.userId)) : null;
    })
    .filter((week): week is StoredPersonWeek => week !== null);
}
